import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useIntl } from 'react-intl';
import { ErrorMessage } from 'types/shared/error-message.type';
import { postOverrideOrder } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/api/create-orders';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { CreateOrderInput, CreateOrderResult } from 'views/order-feed/features/my-supply-plan/types/create-order-input.type';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { buildCreateOrderInput } from 'views/order-feed/features/my-supply-plan/utils/create-order-utils';

/**
 * Create order only for OFD 2, as OFD 1 does not utilize `plantId` and `vessel`, but instead uses `requestedScheduleId`
 */
export const useCreateOrder = () => {
  const {
    account: { company },
  } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const overrideOFDOrder = usePost<CreateOrderResult, { data: CreateOrderInput }, AxiosError<ErrorMessage[]>>(
    (request) => postOverrideOrder(company!.customerErpId, request.data),
    ({ orderNumber }) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });

      queryClient.invalidateQueries({
        queryKey: ['getOrderAssortment']
      });

      createOrderFeedEvent('create', {
        orderNumber: orderNumber,
        supplyPlanType: SupplyPlanType.OFD2,
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: {
          msg: intl.formatMessage({ id: 'snackbar.errors.default' }),
          error: error,
          errorMsg: error.response?.data,
        },
      });
    }
  );

  return { buildOrderObject: buildCreateOrderInput, overrideOFDOrder };
};
